import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Building2, Clock, Gauge, ListChecks, Star } from 'lucide-react';
import { cn } from 'lib/utils';
import { PremiumBadge } from 'components/entitlements/PremiumBadge';
import type { TemplateSummary } from 'modules/templates';

interface TemplateCardProps {
  template: TemplateSummary;
  /** FEATURES keys this template needs that the org's plan lacks. */
  lockedFeatures: string[];
  onOpen: () => void;
}

/** One gallery tile. The whole card opens the preview; the premium badge opens the upgrade dialog. */
export function TemplateCard({ template, lockedFeatures, onOpen }: TemplateCardProps) {
  const { t } = useTranslation();
  return (
    <article
      className={cn(
        'group relative flex h-full flex-col rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow] duration-200',
        'hover:border-foreground/30 hover:shadow-sm',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold leading-snug text-foreground">
          {/* Stretched button: the card is one target without nesting the premium badge inside it. */}
          <button
            type="button"
            onClick={onOpen}
            className="text-left after:absolute after:inset-0 after:rounded-xl focus-visible:!outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
          >
            {template.title}
          </button>
        </h3>
        {template.examStandard ? (
          <span className="inline-flex shrink-0 items-center rounded-md border border-border px-1.5 py-0.5 text-[11px] font-semibold text-foreground/80">
            {template.examStandard}
          </span>
        ) : null}
        {template.scope === 'ORG' ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] font-semibold text-foreground/80">
            <Building2 className="h-3 w-3" aria-hidden />
            {t('templates.our_template')}
          </span>
        ) : template.featured ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
            <Star className="h-3 w-3" aria-hidden />
            {t('templates.featured')}
          </span>
        ) : null}
      </div>
      <p className="mb-4 mt-1.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{template.description}</p>

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <ListChecks className="h-3.5 w-3.5" aria-hidden />
          {t('templates.questions_count', { count: template.questionCount })}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" aria-hidden />
          {t('templates.minutes', { count: template.timeLimitMinutes })}
        </span>
        <span className="inline-flex items-center gap-1">
          <Gauge className="h-3.5 w-3.5" aria-hidden />
          {t(`templates.level_${template.level}`)}
        </span>
        {lockedFeatures.length ? (
          <PremiumBadge
            featureKey={lockedFeatures[0]}
            tooltip={t('templates.premium_tooltip')}
            className="relative z-10 ml-auto"
          />
        ) : (
          <ArrowUpRight
            className="ml-auto h-4 w-4 text-muted-foreground/60 transition-colors [.group:hover_&]:text-foreground"
            aria-hidden
          />
        )}
      </div>
    </article>
  );
}
